import styles from './Rentals.module.css';

import type { FC } from 'react';
import type { ColorVariant } from '../../theme';

import { isEmpty } from 'lodash';
import classNames from 'classnames';
import React, { useMemo } from 'react';

import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { formatAmount } from 'src/utils/money';
import { formatImageUrl } from 'src/utils/format-image';
import { noPhotoAlt, carPhotoAlt } from 'src/utils/imageAlt';

import { useListRentals } from 'src/queries';
import currencyImage from 'src/assets/images/currency.png';

import { Loader } from 'src/components/Loader';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { RequestFailureState, WalletNotLinkedState } from 'src/components/EmptyState';

import { rentalStatusMap } from '../../constants';

/**
 * The renter's own rental deals. Needs a session: the hub route admits the account session and
 * renders this with the gate already passed (app#238, api#402), and the api client sends the
 * account token with the request.
 */
export const MyRentals: FC = () => {
  const query = useListRentals();
  const { data, refetch } = query;
  // An offline renter used to be told they have no rentals (app#210).
  const state = useQueryScreenState(query);
  const items = useMemo(() => data?.items ?? [], [data?.items]);

  if (state === 'loading') {
    return <Loader />;
  }

  // An account without a wallet has no rentals to show yet — said as such, with the way to link
  // one, rather than as "an error on our side" (app#241).
  if (state === 'wallet-not-linked') {
    return <WalletNotLinkedState />;
  }

  if (state !== 'ready') {
    return <RequestFailureState kind={state} onRetry={() => refetch()} />;
  }

  if (isEmpty(items)) {
    return (
      <Stack gap={2} alignItems="center" p={4} mt={8}>
        <Typography color="lightWhite" textAlign="center">
          You have no rentals yet
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack width="100%" gap={4}>
      {items.map((item) => (
        <ListItem
          key={item.id}
          title={`${item.listing.make.name} ${item.listing.model.name}${
            item.listing.year ? ` ${item.listing.year}` : ''
          }`}
          description={item.listing.vin}
          to={`/rental/${item.id}`}
          status={
            <div
              className={styles.status}
              style={{ backgroundColor: rentalStatusMap[item.status]?.color }}
            >
              <Typography
                color={(rentalStatusMap[item.status]?.textColor || 'white') as ColorVariant}
                variant="small"
              >
                {rentalStatusMap[item.status]?.shortLabel}
              </Typography>
            </div>
          }
          image={
            !isEmpty(item.listing.images) ? (
              <img
                src={formatImageUrl(item.listing.images[0])}
                alt={carPhotoAlt(item.listing)}
                className={styles.image}
              />
            ) : (
              <img
                src={currencyImage}
                alt={noPhotoAlt(item.listing)}
                className={classNames([styles.image, styles.defaultImage])}
              />
            )
          }
        >
          <Stack width="100%" gap={1}>
            <Stack
              flexDirection="row"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              flex={1}
            >
              <Typography color="white">Rental:</Typography>
              <Typography color="lightWhite">
                {formatAmount(item.rentalAmount, 6)} {item.cryptoCurrency}
              </Typography>
            </Stack>
            <Stack
              flexDirection="row"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              flex={1}
            >
              <Typography color="white">Deposit:</Typography>
              <Typography color="lightWhite">
                {formatAmount(item.depositAmount, 6)} {item.cryptoCurrency}
              </Typography>
            </Stack>
          </Stack>
        </ListItem>
      ))}
    </Stack>
  );
};
